<template>
    <div>
        <PageDescriptionEditor page-title="Submissions"
            default-description="Always looking for new voices and compelling stories." />

        <div class="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 class="font-header text-[1.5rem] font-bold text-primary mb-4">Submission Guidelines</h2>
            <div class="space-y-4">
                <div>
                    <label class="block font-body text-[1.125rem] leading-[1.75] text-primary mb-2">
                        Guidelines Text
                    </label>
                    <textarea v-model="guidelinesText" rows="6"
                        class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                        placeholder="Enter guidelines text..."></textarea>
                </div>
                <div>
                    <label class="block font-body text-[1.125rem] leading-[1.75] text-primary mb-2">
                        English Guidelines PDF
                    </label>
                    <input type="file" @change="handleGuidelinesFileSelect" accept=".pdf,.doc,.docx"
                        class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
                    <p v-if="guidelinesFileUrl" class="font-body text-[1.125rem] leading-[1.75] text-gray-700 mt-2">
                        Current file: <a :href="guidelinesFileUrl" target="_blank"
                            class="text-green-700 hover:underline">{{ guidelinesFileUrl.split('/').pop() }}</a>
                    </p>
                    <p v-if="guidelinesFileName" class="font-body text-[1.125rem] leading-[1.75] text-gray-700 mt-2">
                        New file selected: {{ guidelinesFileName }}
                    </p>
                </div>
                <div>
                    <label class="block font-body text-[1.125rem] leading-[1.75] text-primary mb-2">
                        French Guidelines PDF
                    </label>
                    <input type="file" @change="handleGuidelinesFileSelectFr" accept=".pdf,.doc,.docx"
                        class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
                    <p v-if="guidelinesFileUrlFr" class="font-body text-[1.125rem] leading-[1.75] text-gray-700 mt-2">
                        Current file: <a :href="guidelinesFileUrlFr" target="_blank"
                            class="text-green-700 hover:underline">{{ guidelinesFileUrlFr.split('/').pop() }}</a>
                    </p>
                    <p v-if="guidelinesFileNameFr" class="font-body text-[1.125rem] leading-[1.75] text-gray-700 mt-2">
                        New file selected: {{ guidelinesFileNameFr }}
                    </p>
                </div>
                <button @click="saveGuidelines" :disabled="savingGuidelines"
                    class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-[1.125rem] leading-[1.75] disabled:opacity-50 disabled:cursor-not-allowed">
                    {{ savingGuidelines ? 'Saving...' : 'Save Guidelines' }}
                </button>
            </div>
        </div>

        <h2 class="font-header text-[3rem] font-bold text-primary mb-6">Manuscript Submissions</h2>

        <div class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="p-6 border-b border-creme-200 flex justify-between items-center">
                <h3 class="font-header text-[1.5rem] font-bold">Submissions ({{ submissions.length }})</h3>
                <button @click="() => loadSubmissions()"
                    class="px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-[1.125rem] leading-[1.75]">
                    Refresh
                </button>
            </div>
            <div class="max-h-[calc(100vh-300px)] overflow-y-auto">
                <div class="w-full overflow-x-auto">
                    <table class="w-full min-w-max">
                        <thead class="bg-green-700 text-white sticky top-0">
                            <tr>
                                <th class="px-4 py-3 text-left font-body text-[1.125rem] leading-[1.75]">Title</th>
                                <th class="px-4 py-3 text-left font-body text-[1.125rem] leading-[1.75]">Author</th>
                                <th class="px-4 py-3 text-left font-body text-[1.125rem] leading-[1.75]">Email</th>
                                <th class="px-4 py-3 text-left font-body text-[1.125rem] leading-[1.75]">Genre</th>
                                <th class="px-4 py-3 text-left font-body text-[1.125rem] leading-[1.75]">Date</th>
                                <th class="px-4 py-3 text-left font-body text-[1.125rem] leading-[1.75]">Sample</th>
                                <th class="px-4 py-3 text-left font-body text-[1.125rem] leading-[1.75]">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="submission in submissions" :key="submission.id"
                                class="border-b border-creme-200 hover:bg-creme-50 cursor-pointer"
                                @click="selectedSubmission = submission">
                                <td class="px-4 py-3 font-body text-[1.125rem] leading-[1.75] text-gray-900">{{
                                    submission.title }}</td>
                                <td class="px-4 py-3 font-body text-[1.125rem] leading-[1.75] text-gray-900">{{
                                    submission.author_name }}</td>
                                <td class="px-4 py-3 font-body text-[1.125rem] leading-[1.75] text-gray-900">{{
                                    submission.email }}</td>
                                <td class="px-4 py-3 font-body text-[1.125rem] leading-[1.75] capitalize text-gray-900">
                                    {{ submission.genre }}</td>
                                <td class="px-4 py-3 font-body text-[1.125rem] leading-[1.75] text-gray-900">{{
                                    formatDate(submission.created_at) }}</td>
                                <td class="px-4 py-3">
                                    <button v-if="submission.manuscript_file_url"
                                        @click.stop="downloadManuscript(submission.manuscript_file_url)"
                                        class="text-green-700 hover:text-green-800 font-body text-[1.125rem] leading-[1.75]">
                                        Download
                                    </button>
                                    <span v-else
                                        class="text-gray-400 font-body text-[1.125rem] leading-[1.75]">N/A</span>
                                </td>
                                <td class="px-4 py-3">
                                    <button @click.stop="deleteSubmission(submission.id)"
                                        class="text-red-600 hover:text-red-800 font-body text-[1.125rem] leading-[1.75]">
                                        Delete
                                    </button>
                                </td>
                            </tr>
                            <tr v-if="submissions.length === 0">
                                <td colspan="7" class="px-4 py-8 text-center text-gray-500 font-marketing">No manuscript
                                    submissions yet</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div v-if="pagination && pagination.total_pages > 1"
                class="p-4 flex justify-between items-center border-t border-creme-200">
                <button @click="loadSubmissions(currentPage - 1)" :disabled="!pagination.prev_page"
                    :class="['px-4 py-2 rounded-md font-marketing', pagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                    Previous
                </button>
                <span class="font-marketing text-gray-700">
                    Page {{ pagination.page }} of {{ pagination.total_pages }} ({{ pagination.total }} total)
                </span>
                <button @click="loadSubmissions(currentPage + 1)" :disabled="!pagination.next_page"
                    :class="['px-4 py-2 rounded-md font-marketing', pagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                    Next
                </button>
            </div>
        </div>

        <BaseModal v-model="showSubmissionModal" content-class="max-w-3xl max-h-[90vh]">
            <h3 class="text-2xl font-marketing font-bold text-primary mb-6">Manuscript Submission Details</h3>
            <div v-if="selectedSubmission" class="space-y-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label class="text-sm font-marketing font-semibold text-gray-600">Title</label>
                        <p class="text-lg font-sans text-primary">{{ selectedSubmission.title }}</p>
                    </div>
                    <div>
                        <label class="text-sm font-marketing font-semibold text-gray-600">Author</label>
                        <p class="text-lg font-sans text-primary">{{ selectedSubmission.author_name ||
                            `${selectedSubmission.first_name || ''} ${selectedSubmission.last_name || ''}`.trim() ||
                            'N/A' }}</p>
                    </div>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label class="text-sm font-marketing font-semibold text-gray-600">Email</label>
                        <p class="text-lg font-sans text-primary">
                            <a :href="`mailto:${selectedSubmission.email}`" class="text-green-700 hover:underline">
                                {{ selectedSubmission.email }}
                            </a>
                        </p>
                    </div>
                    <div>
                        <label class="text-sm font-marketing font-semibold text-gray-600">Genre</label>
                        <p class="text-lg font-sans text-primary capitalize">{{ selectedSubmission.genre }}</p>
                    </div>
                </div>
                <div>
                    <label class="text-sm font-marketing font-semibold text-gray-600">Manuscript Sample</label>
                    <div class="mt-2">
                        <button v-if="selectedSubmission.manuscript_file_url"
                            @click="downloadManuscript(selectedSubmission.manuscript_file_url)"
                            class="inline-flex items-center px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing">
                            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            Download Manuscript Sample
                        </button>
                        <p v-else class="text-gray-500 font-sans">No manuscript sample available</p>
                    </div>
                </div>
                <div>
                    <label class="text-sm font-marketing font-semibold text-gray-600">Letter of Proposal</label>
                    <div class="mt-2">
                        <button v-if="selectedSubmission.letter_of_proposal_url"
                            @click="downloadManuscript(selectedSubmission.letter_of_proposal_url)"
                            class="inline-flex items-center px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing">
                            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            Download Letter of Proposal
                        </button>
                        <p v-else class="text-gray-500 font-sans">No letter of proposal provided</p>
                    </div>
                </div>
                <div v-if="selectedSubmission.additional_info">
                    <label class="text-sm font-marketing font-semibold text-gray-600">Additional Information</label>
                    <p class="text-lg font-sans text-primary whitespace-pre-wrap bg-creme-50 p-4 rounded-md">{{
                        selectedSubmission.additional_info }}</p>
                </div>
                <div>
                    <label class="text-sm font-marketing font-semibold text-gray-600">Submitted</label>
                    <p class="text-lg font-sans text-primary">{{ formatDate(selectedSubmission.created_at) }}</p>
                </div>
            </div>
        </BaseModal>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { adminService } from '../../services/admin'
import { publicService } from '../../services/public'
import PageDescriptionEditor from './PageDescriptionEditor.vue'
import BaseModal from './BaseModal.vue'
import { formatDateTime } from '../../utils/date'

const submissions = ref([])
const selectedSubmission = ref(null)
const currentPage = ref(1)
const limit = ref(10)
const pagination = ref(null)
const submissionsPage = ref(null)
const guidelinesText = ref('')
const guidelinesFileUrl = ref('')
const guidelinesFileName = ref('')
const guidelinesFile = ref(null)
const guidelinesFileUrlFr = ref('')
const guidelinesFileNameFr = ref('')
const guidelinesFileFr = ref(null)
const savingGuidelines = ref(false)

const showSubmissionModal = computed({
    get: () => !!selectedSubmission.value,
    set: (value) => {
        if (!value) selectedSubmission.value = null
    }
})

const loadSubmissions = async (page = 1) => {
    try {
        const response = await adminService.getManuscriptSubmissions(page, limit.value)
        console.log('Manuscript submissions response:', response)
        if (response.data && response.pagination) {
            submissions.value = response.data.map(submission => ({
                ...submission,
                author_name: `${submission.first_name || ''} ${submission.last_name || ''}`.trim() || 'N/A'
            }))
            pagination.value = response.pagination
            currentPage.value = page
        } else {
            const data = response.data || response
            if (Array.isArray(data)) {
                submissions.value = data.map(submission => ({
                    ...submission,
                    author_name: `${submission.first_name || ''} ${submission.last_name || ''}`.trim() || 'N/A'
                }))
            } else {
                console.error('Unexpected response format:', response)
                submissions.value = []
            }
            pagination.value = null
        }
    } catch (error) {
        console.error('Failed to load manuscript submissions:', error)
        const errorMessage = error.response?.data?.error || error.message || 'Unknown error'
        submissions.value = []
        pagination.value = null
        alert(`Failed to load manuscript submissions: ${errorMessage}`)
    }
}

const deleteSubmission = async (id) => {
    if (!confirm('Are you sure you want to delete this manuscript submission?')) {
        return
    }

    try {
        await adminService.deleteManuscriptSubmission(id)
        submissions.value = submissions.value.filter(s => s.id !== id)
        if (selectedSubmission.value?.id === id) {
            selectedSubmission.value = null
        }
        if (submissions.value.length === 0 && pagination.value && currentPage.value > 1) {
            await loadSubmissions(currentPage.value - 1)
        } else {
            await loadSubmissions(currentPage.value)
        }
    } catch (error) {
        console.error('Failed to delete manuscript submission:', error)
        alert('Failed to delete manuscript submission. Please try again.')
    }
}

const downloadManuscript = async (fileUrl) => {
    if (!fileUrl) return

    try {
        const signedUrl = await adminService.getManuscriptSignedUrl(fileUrl)
        if (signedUrl) {
            window.open(signedUrl, '_blank')
        } else {
            alert('Failed to generate download link. Please try again.')
        }
    } catch (error) {
        console.error('Failed to get signed URL:', error)
        alert('Failed to generate download link. Please try again.')
    }
}

const formatDate = formatDateTime

const loadSubmissionsPage = async () => {
    try {
        const page = await publicService.getPage('Submissions')
        submissionsPage.value = page
        guidelinesText.value = page.guidelines_text || ''
        guidelinesFileUrl.value = page.guidelines_file_url || ''
        guidelinesFileUrlFr.value = page.guidelines_file_url_fr || ''
    } catch (error) {
        console.error('Failed to load submissions page:', error)
    }
}

const handleGuidelinesFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        if (file.size > 10 * 1024 * 1024) {
            alert('File size must be less than 10MB')
            event.target.value = ''
            return
        }
        guidelinesFileName.value = file.name
        guidelinesFile.value = file
    }
}

const handleGuidelinesFileSelectFr = (event) => {
    const file = event.target.files[0]
    if (file) {
        if (file.size > 10 * 1024 * 1024) {
            alert('File size must be less than 10MB')
            event.target.value = ''
            return
        }
        guidelinesFileNameFr.value = file.name
        guidelinesFileFr.value = file
    }
}

const saveGuidelines = async () => {
    savingGuidelines.value = true
    try {
        let fileUrl = guidelinesFileUrl.value
        let fileUrlFr = guidelinesFileUrlFr.value

        // Upload English file if selected
        if (guidelinesFile.value) {
            try {
                const uploadResult = await adminService.uploadGuidelinesFile(guidelinesFile.value)
                fileUrl = uploadResult.url
            } catch (error) {
                alert(error.response?.data?.error || error.message || 'Failed to upload guidelines file.')
                savingGuidelines.value = false
                return
            }
        }

        // Upload French file if selected
        if (guidelinesFileFr.value) {
            try {
                const uploadResult = await adminService.uploadGuidelinesFile(guidelinesFileFr.value)
                fileUrlFr = uploadResult.url
            } catch (error) {
                alert(error.response?.data?.error || error.message || 'Failed to upload French guidelines file.')
                savingGuidelines.value = false
                return
            }
        }

        if (!submissionsPage.value) {
            await adminService.createPage({
                title: 'Submissions',
                guidelines_text: guidelinesText.value || null,
                guidelines_file_url: fileUrl || null,
                guidelines_file_url_fr: fileUrlFr || null
            })
        } else {
            await adminService.updatePage(submissionsPage.value.id, {
                guidelines_text: guidelinesText.value || null,
                guidelines_file_url: fileUrl || null,
                guidelines_file_url_fr: fileUrlFr || null
            })
        }

        guidelinesFileUrl.value = fileUrl
        guidelinesFileName.value = ''
        guidelinesFile.value = null
        guidelinesFileUrlFr.value = fileUrlFr
        guidelinesFileNameFr.value = ''
        guidelinesFileFr.value = null

        const fileInputs = document.querySelectorAll('input[type="file"]')
        fileInputs.forEach(input => input.value = '')

        alert('Guidelines saved successfully!')
        await loadSubmissionsPage()
    } catch (error) {
        console.error('Failed to save guidelines:', error)
        alert('Failed to save guidelines. Please try again.')
    } finally {
        savingGuidelines.value = false
    }
}

onMounted(() => {
    loadSubmissions(1)
    loadSubmissionsPage()
})
</script>

<style scoped></style>
